import {
	addUnitSynonym,
	deleteUnitSynonym,
	getUnitSynonyms,
	updateUnitSynonym,
} from "@/services/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit2, Loader2, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";

interface EditingState {
	synonymId: number | null;
	value: string;
}

export function SynonymDictionary() {
	const queryClient = useQueryClient();
	const [addingTo, setAddingTo] = useState<string | null>(null);
	const [newSynonym, setNewSynonym] = useState("");
	const [newCanonicalUnit, setNewCanonicalUnit] = useState("");
	const [editing, setEditing] = useState<EditingState>({ synonymId: null, value: "" });
	const [loading, setLoading] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ["unit-synonyms"],
		queryFn: getUnitSynonyms,
	});

	const handleAddSynonym = async (canonicalUnit: string, synonym: string) => {
		if (!synonym.trim()) {
			setError("Синоним не может быть пустым");
			return;
		}

		setLoading(`add-${canonicalUnit}`);
		setError(null);

		try {
			await addUnitSynonym(canonicalUnit, synonym);
			await queryClient.invalidateQueries({ queryKey: ["unit-synonyms"] });
			setNewSynonym("");
			setAddingTo(null);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Ошибка добавления синонима");
		} finally {
			setLoading(null);
		}
	};

	const handleAddNewUnit = async () => {
		if (!newCanonicalUnit.trim()) {
			setError("Каноническая единица не может быть пустой");
			return;
		}
		if (!newSynonym.trim()) {
			setError("Синоним не может быть пустым");
			return;
		}

		setLoading("add-new-unit");
		setError(null);

		try {
			await addUnitSynonym(newCanonicalUnit, newSynonym);
			await queryClient.invalidateQueries({ queryKey: ["unit-synonyms"] });
			setNewCanonicalUnit("");
			setNewSynonym("");
		} catch (err) {
			setError(err instanceof Error ? err.message : "Ошибка добавления синонима");
		} finally {
			setLoading(null);
		}
	};

	const handleUpdateSynonym = async (id: number, synonym: string) => {
		if (!synonym.trim()) {
			setError("Синоним не может быть пустым");
			return;
		}

		setLoading(`update-${id}`);
		setError(null);

		try {
			await updateUnitSynonym(id, synonym);
			await queryClient.invalidateQueries({ queryKey: ["unit-synonyms"] });
			setEditing({ synonymId: null, value: "" });
		} catch (err) {
			setError(err instanceof Error ? err.message : "Ошибка обновления синонима");
		} finally {
			setLoading(null);
		}
	};

	const handleDeleteSynonym = async (id: number) => {
		if (!confirm("Удалить этот синоним?")) return;

		setLoading(`delete-${id}`);
		setError(null);

		try {
			await deleteUnitSynonym(id);
			await queryClient.invalidateQueries({ queryKey: ["unit-synonyms"] });
		} catch (err) {
			setError(err instanceof Error ? err.message : "Ошибка удаления синонима");
		} finally {
			setLoading(null);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center p-8 text-slate-500">
				<Loader2 className="mr-2 h-5 w-5 animate-spin" />
				Загрузка словаря...
			</div>
		);
	}

	const synonymGroups = data?.synonyms ?? [];

	return (
		<div className="space-y-4">
			<div>
				<h2 className="text-xl font-semibold text-slate-900">Словарь синонимов единиц измерения</h2>
				<p className="mt-1 text-sm text-slate-500">
					Управление альтернативными написаниями единиц измерения из книги расценок
				</p>
			</div>

			{error && (
				<div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
					{error}
				</div>
			)}

			{/* Add new canonical unit */}
			<div className="rounded-lg border border-slate-200 bg-white p-4">
				<h3 className="mb-3 text-sm font-medium text-slate-700">Добавить новую единицу</h3>
				<div className="flex gap-2">
					<input
						type="text"
						value={newCanonicalUnit}
						onChange={(e) => setNewCanonicalUnit(e.target.value)}
						placeholder="Каноническая единица (например, кв.м)"
						className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					/>
					<input
						type="text"
						value={newSynonym}
						onChange={(e) => setNewSynonym(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleAddNewUnit();
							}
						}}
						placeholder="Синоним (например, м2)"
						className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					/>
					<button
						type="button"
						onClick={handleAddNewUnit}
						disabled={loading === "add-new-unit"}
						className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{loading === "add-new-unit" ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Plus className="h-4 w-4" />
						)}
						Добавить
					</button>
				</div>
			</div>

			{/* Synonym groups */}
			{synonymGroups.length === 0 ? (
				<div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500">
					Словарь синонимов пуст. Добавьте первую единицу измерения выше.
				</div>
			) : (
				<div className="space-y-3">
					{synonymGroups.map((group) => (
						<div key={group.canonicalUnit} className="rounded-lg border border-slate-200 bg-white">
							<div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
								<div className="flex items-center justify-between">
									<h3 className="font-medium text-slate-900">
										{group.canonicalUnit}
										<span className="ml-2 text-sm font-normal text-slate-500">
											({group.synonyms.length}{" "}
											{group.synonyms.length === 1 ? "синоним" : "синонимов"})
										</span>
									</h3>
									<button
										type="button"
										onClick={() =>
											setAddingTo((prev) =>
												prev === group.canonicalUnit ? null : group.canonicalUnit,
											)
										}
										className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-200"
									>
										<Plus className="h-3 w-3" />
										Добавить синоним
									</button>
								</div>
							</div>

							<div className="divide-y divide-slate-100">
								{/* Add new synonym row */}
								{addingTo === group.canonicalUnit && (
									<div className="px-4 py-3 bg-blue-50">
										<div className="flex gap-2">
											<input
												type="text"
												value={newSynonym}
												onChange={(e) => setNewSynonym(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														handleAddSynonym(group.canonicalUnit, newSynonym);
													} else if (e.key === "Escape") {
														setAddingTo(null);
														setNewSynonym("");
													}
												}}
												placeholder="Введите синоним"
												autoFocus
												className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
											/>
											<button
												type="button"
												onClick={() => handleAddSynonym(group.canonicalUnit, newSynonym)}
												disabled={loading === `add-${group.canonicalUnit}`}
												className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
											>
												{loading === `add-${group.canonicalUnit}` ? (
													<Loader2 className="h-4 w-4 animate-spin" />
												) : (
													"Добавить"
												)}
											</button>
											<button
												type="button"
												onClick={() => {
													setAddingTo(null);
													setNewSynonym("");
												}}
												className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-100"
											>
												<X className="h-4 w-4" />
											</button>
										</div>
									</div>
								)}

								{/* Existing synonyms */}
								{group.synonyms.map((synItem) => {
									const isEditing = editing.synonymId === synItem.id;

									return (
										<div key={synItem.id} className="px-4 py-2.5 flex items-center justify-between">
											{isEditing ? (
												<div className="flex flex-1 gap-2">
													<input
														type="text"
														value={editing.value}
														onChange={(e) => setEditing({ ...editing, value: e.target.value })}
														onKeyDown={(e) => {
															if (e.key === "Enter") {
																handleUpdateSynonym(synItem.id, editing.value);
															} else if (e.key === "Escape") {
																setEditing({ synonymId: null, value: "" });
															}
														}}
														autoFocus
														className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
													/>
													<button
														type="button"
														onClick={() => handleUpdateSynonym(synItem.id, editing.value)}
														disabled={loading === `update-${synItem.id}`}
														className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white transition hover:bg-blue-700 disabled:opacity-50"
													>
														{loading === `update-${synItem.id}` ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															"Сохранить"
														)}
													</button>
													<button
														type="button"
														onClick={() => setEditing({ synonymId: null, value: "" })}
														className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-100"
													>
														Отмена
													</button>
												</div>
											) : (
												<>
													<span className="text-sm text-slate-700 font-mono">
														{synItem.synonym}
													</span>
													<div className="flex gap-1">
														<button
															type="button"
															onClick={() =>
																setEditing({ synonymId: synItem.id, value: synItem.synonym })
															}
															className="rounded p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
															title="Редактировать"
														>
															<Edit2 className="h-4 w-4" />
														</button>
														<button
															type="button"
															onClick={() => handleDeleteSynonym(synItem.id)}
															disabled={loading === `delete-${synItem.id}`}
															className="rounded p-1.5 text-slate-500 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
															title="Удалить"
														>
															{loading === `delete-${synItem.id}` ? (
																<Loader2 className="h-4 w-4 animate-spin" />
															) : (
																<Trash2 className="h-4 w-4" />
															)}
														</button>
													</div>
												</>
											)}
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
